/**
 * Minimal HTTP plumbing shared by every data-source package.
 *
 * Scope is deliberately narrow: build a request, bound how long it may take,
 * retry transient failures, and stay polite to public APIs. It knows nothing
 * about trails, species or caching.
 */

export class HttpError extends Error {
  readonly status: number;
  readonly url: string;

  constructor(status: number, url: string) {
    super(`HTTP ${status} for ${url}`);
    this.name = "HttpError";
    this.status = status;
    this.url = url;
  }
}

/** A drop-in replacement for `globalThis.fetch`, so callers can inject a proxy. */
export type FetchLike = (input: string, init?: RequestInit) => Promise<Response>;

export type RequestOptions = {
  /** Abandon the request after this many milliseconds. */
  timeoutMs?: number;
  /** Extra attempts after the first failure. Only retries timeouts, network errors and 5xx/429. */
  retries?: number;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  /** Serialises requests; pair with {@link createRateLimiter} to respect published API etiquette. */
  limiter?: Limiter;
  fetchImpl?: FetchLike;
};

const DEFAULT_TIMEOUT_MS = 12_000;

/**
 * `User-Agent` is a forbidden header name in browsers: setting it throws or is
 * silently dropped. Data-source packages still want to identify themselves when
 * running under Node, so they pass it through here and we drop it in the browser.
 */
const FORBIDDEN_IN_BROWSER = new Set(["user-agent", "referer", "origin", "host"]);

const isBrowser = (): boolean =>
  typeof window !== "undefined" && typeof window.document !== "undefined";

function safeHeaders(headers: Record<string, string> | undefined): Record<string, string> {
  if (!headers) return {};
  if (!isBrowser()) return { ...headers };
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(headers)) {
    if (!FORBIDDEN_IN_BROWSER.has(key.toLowerCase())) out[key] = value;
  }
  return out;
}

const retryable = (status: number): boolean => status === 429 || status >= 500;

const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

/** Combine an external abort signal with our own timeout. */
function linkSignals(timeout: AbortController, external: AbortSignal | undefined): () => void {
  if (!external) return () => {};
  if (external.aborted) {
    timeout.abort();
    return () => {};
  }
  const onAbort = () => timeout.abort();
  external.addEventListener("abort", onAbort, { once: true });
  return () => external.removeEventListener("abort", onAbort);
}

/** Perform a request with timeout + retry. Throws {@link HttpError} on a non-2xx response. */
export async function request(
  url: string,
  init: RequestInit = {},
  options: RequestOptions = {},
): Promise<Response> {
  const {
    timeoutMs = DEFAULT_TIMEOUT_MS,
    retries = 1,
    headers,
    signal,
    limiter,
    fetchImpl = globalThis.fetch,
  } = options;

  const run = async (): Promise<Response> => {
    let lastError: unknown;

    for (let attempt = 0; attempt <= retries; attempt++) {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), timeoutMs);
      const unlink = linkSignals(controller, signal);
      try {
        const response = await fetchImpl(url, {
          ...init,
          headers: { ...safeHeaders(headers), ...safeHeaders(init.headers as Record<string, string>) },
          signal: controller.signal,
        });
        if (!response.ok) {
          if (attempt < retries && retryable(response.status)) {
            lastError = new HttpError(response.status, url);
            await sleep(250 * 2 ** attempt);
            continue;
          }
          throw new HttpError(response.status, url);
        }
        return response;
      } catch (error) {
        lastError = error;
        // An explicit caller abort is final; timeouts and network errors are retried.
        if (signal?.aborted) throw error;
        // A status we already classified as final must not be retried by the
        // generic path: the throw above lands here rather than escaping.
        if (error instanceof HttpError && !retryable(error.status)) throw error;
        if (attempt >= retries) throw error;
        await sleep(250 * 2 ** attempt);
      } finally {
        clearTimeout(timer);
        unlink();
      }
    }

    throw lastError instanceof Error ? lastError : new Error(`request failed: ${url}`);
  };

  return limiter ? limiter(run) : run();
}

/** GET a URL and parse the body as JSON. */
export async function getJson<T>(url: string, options: RequestOptions = {}): Promise<T> {
  const response = await request(url, { headers: { Accept: "application/json" } }, options);
  return (await response.json()) as T;
}

/** GET a URL and return the body as text. */
export async function getText(url: string, options: RequestOptions = {}): Promise<string> {
  const response = await request(url, {}, options);
  return await response.text();
}

/** POST a form-encoded body and parse the response as JSON. */
export async function postForm<T>(
  url: string,
  form: Record<string, string>,
  options: RequestOptions = {},
): Promise<T> {
  const body = new URLSearchParams(form).toString();
  const response = await request(
    url,
    {
      method: "POST",
      body,
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        Accept: "application/json",
      },
    },
    options,
  );
  return (await response.json()) as T;
}

/** POST a JSON body and parse the response as JSON. */
export async function postJson<T>(
  url: string,
  payload: unknown,
  options: RequestOptions = {},
): Promise<T> {
  const response = await request(
    url,
    {
      method: "POST",
      body: JSON.stringify(payload),
      headers: { "Content-Type": "application/json", Accept: "application/json" },
    },
    options,
  );
  return (await response.json()) as T;
}

/**
 * Resolve to `null` instead of throwing. Data sources are best-effort by nature:
 * a missing forecast or a rate-limited species lookup should degrade the page,
 * not break it.
 */
export async function orNull<T>(promise: Promise<T>): Promise<T | null> {
  try {
    return await promise;
  } catch {
    return null;
  }
}

export type Limiter = <T>(task: () => Promise<T>) => Promise<T>;

/**
 * Cap concurrency and enforce a minimum gap between task starts. iNaturalist and
 * Overpass both ask clients to stay well under a request per second.
 */
export function createRateLimiter(options: {
  concurrency?: number;
  minIntervalMs?: number;
} = {}): Limiter {
  const { concurrency = 4, minIntervalMs = 0 } = options;
  const queue: Array<() => void> = [];
  let active = 0;
  let lastStart = 0;

  const next = (): void => {
    if (active >= concurrency) return;
    const task = queue.shift();
    if (!task) return;
    active++;
    task();
  };

  return <T>(task: () => Promise<T>): Promise<T> =>
    new Promise<T>((resolve, reject) => {
      const start = async (): Promise<void> => {
        // Claim the slot before yielding. Computing the wait and only recording
        // the start after sleeping lets every free slot queue behind the same
        // timestamp and then fire together, which is not a minimum interval.
        const now = Date.now();
        const startAt = Math.max(now, lastStart + minIntervalMs);
        lastStart = startAt;
        if (startAt > now) await sleep(startAt - now);
        try {
          resolve(await task());
        } catch (error) {
          reject(error);
        } finally {
          active--;
          next();
        }
      };
      queue.push(() => void start());
      next();
    });
}
